import React from 'react';
import { useTranslation } from 'react-i18next';
import { AlarmClock, CheckCircle, Clock, HelpCircle, XCircle, type LucideIcon } from 'lucide-react';
import StatusBadge from '@/components/ui/StatusBadge';
import { AssignmentResponse, resolveEnum } from '@/models/tournament';
import { parseApiUtc } from '@/utils/apiDate';
import { appDateTime } from '@/utils/appDateFormat';

/**
 * CO-764 — one chip for "has this official answered?", used on every roster HR looks at.
 *
 * Until now `ResponseStatus` never left the server on a match roster, so no HR screen could answer
 * *"who has not confirmed for tomorrow?"*; the only signal was the notification stream and the roster
 * silently shrinking when the deadline job archived someone.
 *
 * 🔴 The mapping is TOTAL by construction. `RESPONSE_CHIPS` is a `Record<AssignmentResponse, …>`, so a
 * new enum member fails the TYPE CHECK until it is given a chip here — and anything that is not a
 * recognised member at RUNTIME (a server ahead of this client, a hand-rolled fixture) falls back to the
 * neutral "unknown" chip. Neither path can render blank, which is how `Lapsed` would otherwise have
 * shown up: as nothing at all.
 *
 * This reuses the design system's `StatusBadge` — no new visual language.
 */

type ChipVariant = 'success' | 'error' | 'warning' | 'info' | 'neutral';

interface ChipSpec {
  variant: ChipVariant;
  icon: LucideIcon;
  /** i18n key in the `tournament` namespace. A complete phrase — never an enum name passed through. */
  labelKey: string;
}

/**
 * State → chip. Four distinct readings, and `Lapsed` is deliberately NOT red:
 * per the owner's CO-762 decision an expiry LAPSES an assignment rather than declining it — the
 * official keeps the slot, the shift and the pay, and can still accept. It needs a nudge, not an alarm.
 */
const RESPONSE_CHIPS: Record<AssignmentResponse, ChipSpec> = {
  [AssignmentResponse.Pending]: { variant: 'info', icon: Clock, labelKey: 'Awaiting response' },
  [AssignmentResponse.Accepted]: { variant: 'success', icon: CheckCircle, labelKey: 'Accepted' },
  [AssignmentResponse.Declined]: { variant: 'error', icon: XCircle, labelKey: 'Declined' },
  [AssignmentResponse.Lapsed]: { variant: 'warning', icon: AlarmClock, labelKey: 'Response overdue' },
};

/** Anything the client does not recognise — visible and honest, never a blank cell. */
const UNKNOWN_CHIP: ChipSpec = { variant: 'neutral', icon: HelpCircle, labelKey: 'Response unknown' };

export interface AssignmentResponseChipProps {
  /** `RefereeTeamMemberView.responseStatus` — the enum name or its number. */
  responseStatus?: AssignmentResponse | number | string | null;
  /** `RefereeTeamMemberView.responseDeadline` — a UTC instant, or null when none was set. */
  responseDeadline?: string | null;
  /**
   * `MatchView.hasStarted`, stamped server-side against the COMPANY timezone (`MatchService`).
   * Kickoff is never recomputed here — it is only used to know that a still-open deadline is in the past.
   */
  matchHasStarted?: boolean;
  /**
   * Render the deadline as muted text beside the chip (roster cards with room for it). It is only
   * shown while the deadline still means something — Pending and Lapsed. On an answered assignment
   * the date is history, so it stays in the chip's tooltip instead of adding a line to every row.
   */
  showDeadline?: boolean;
  size?: 'sm' | 'default' | 'lg';
  className?: string;
  testId?: string;
}

const AssignmentResponseChip: React.FC<AssignmentResponseChipProps> = ({
  responseStatus,
  responseDeadline,
  matchHasStarted,
  showDeadline = false,
  size = 'sm',
  className,
  testId,
}) => {
  const { t } = useTranslation('tournament');

  // A missing field means an older server build that does not send it at all — show nothing rather
  // than claim "unknown" on every row. A field that IS present but unrecognised gets the unknown chip.
  if (responseStatus === null || responseStatus === undefined) return null;

  const resolved = resolveEnum(AssignmentResponse, responseStatus);
  const spec =
    resolved !== undefined && resolved in RESPONSE_CHIPS
      ? RESPONSE_CHIPS[resolved as AssignmentResponse]
      : UNKNOWN_CHIP;

  const deadline = parseApiUtc(responseDeadline ?? null);
  // The deadline is an INSTANT (the server compares it to UtcNow), so it is formatted in the viewer's
  // own clock via appDateTime — dd-mm-yyyy HH:mm, the system format. Never rendered as a raw UTC string.
  const deadlineText = deadline ? appDateTime(deadline) : '';
  const deadlineIsPast =
    !!deadline &&
    (matchHasStarted === true ||
      resolved === AssignmentResponse.Lapsed ||
      deadline.getTime() <= Date.now());
  // Whole sentences, both tenses — not a fragment with a date glued on.
  const deadlineLabel = deadlineText
    ? deadlineIsPast
      ? t('Response was due by {{deadline}}', { deadline: deadlineText })
      : t('Response due by {{deadline}}', { deadline: deadlineText })
    : '';

  // The date is only rendered as text while it still means something — an answered assignment keeps
  // it in the tooltip. Decided BEFORE the chip so the caller's className lands on whichever element
  // ends up being the outermost one.
  const withDeadlineText =
    showDeadline &&
    !!deadlineLabel &&
    (resolved === AssignmentResponse.Pending || resolved === AssignmentResponse.Lapsed);

  const chip = (
    <StatusBadge
      variant={spec.variant}
      size={size}
      icon={spec.icon}
      className={withDeadlineText ? undefined : className}
      title={deadlineLabel || undefined}
      data-testid={testId}
    >
      {t(spec.labelKey)}
    </StatusBadge>
  );

  if (!withDeadlineText) return chip;

  return (
    <span className={`inline-flex items-center gap-1.5 flex-wrap ${className ?? ''}`}>
      {chip}
      <span className="text-[11px] text-muted-foreground">{deadlineLabel}</span>
    </span>
  );
};

export default AssignmentResponseChip;
export { AssignmentResponseChip };
